import React, { useEffect, useState } from 'react';
import { Drawer, Tooltip, Popconfirm } from 'antd';
import { Button } from '@jcm/design-system';
import { CloseOutlined, ReloadOutlined } from '@ant-design/icons';

import CustomAlert from '../../../../../../shared/components/customAlert/customAlert';
import BarraDeProgresso from '../../../components/ProgressBar/BarraDeProgresso';
import ModalContainer from '../../../components/modalContainer/ModalContainer';
import ModalEdicaoEmMassa from '../ModalEdicaoEmMassa';

import estilo from './index.module.scss';
import ObrigacoesService from '../../../services/Obrigacoes/Obrigacoes.service';
import ModalConfirmacao from '../ModalConfirmacao/ModalConfirmacao';

type AlertType = {
	type: 'error' | 'success' | 'info';
	content: string;
	title?: string;
	key?: number;
};

interface BarraAcoesProps {
	empresaId: number | string;
	obrigacoesSelecionadas: number[];
	onSelectChange: (selecionados: number[]) => void;
	setMostrarSelecionados: (mostrar: boolean) => void;
	aoAtualizar?: () => void;
}

const noop = (): void => undefined;

const BarraAcoes: React.FC<BarraAcoesProps> = ({
	empresaId,
	obrigacoesSelecionadas,
	onSelectChange,
	setMostrarSelecionados,
	aoAtualizar,
}) => {
	const [drawerVisivel, setDrawerVisivel] = useState(false);
	const [alerta, setAlerta] = useState<AlertType | null>(null);
	const [modalConfirmacaoVisivel, setModalConfirmacaoVisivel] = useState(false);
	const [progresso, setProgresso] = useState(0);
	const [tempoEstimado, setTempoEstimado] = useState<number | undefined>(undefined);
	const [activeKeys, setActiveKeys] = useState<string[]>([]);
	const [resetForm, setResetForm] = useState<() => void>(noop);
	const [isExcluindo, setIsExcluindo] = useState(false);
	const [popconfirmVisivel, setPopconfirmVisivel] = useState(false);

	const obrigacoesService = new ObrigacoesService();

	const exibirAlerta = (tipo: AlertType['type'], conteudo: string, titulo?: string) => {
		setAlerta({
			type: tipo,
			content: conteudo,
			title: titulo,
			key: Date.now(),
		});
	};

	const abrirModalConfirmacao = () => {
		if (obrigacoesSelecionadas.length < 1) {
			exibirAlerta('info', 'Selecione ao menos uma obrigação para excluir.', 'Atenção!');
			return;
		}
		setModalConfirmacaoVisivel(true);
	};

	const abrirDrawerEdicao = () => {
		if (obrigacoesSelecionadas.length < 1) {
			exibirAlerta('info', 'Selecione ao menos uma obrigação para editar.', 'Atenção!');
			return;
		}
		setDrawerVisivel(true);
	};

	const confirmarExclusao = async () => {
		setModalConfirmacaoVisivel(false);
		setIsExcluindo(true);
		setProgresso(0);

		const totalObrigacoes = obrigacoesSelecionadas.length;
		const incremento = Math.round((100 / totalObrigacoes) * 100) / 100;

		setTempoEstimado(Number((totalObrigacoes * 0.5).toFixed(2)));

		try {
			for (let i = 0; i < totalObrigacoes; i++) {
				await obrigacoesService.excluirObrigacoesEmMassa(empresaId, [obrigacoesSelecionadas[i]]);
				setProgresso((prev) => Math.min(prev + incremento, 100));
			}

			exibirAlerta('success', 'Obrigações excluídas com sucesso!', 'Sucesso');
			onSelectChange([]);
			setMostrarSelecionados(false);
			await aoAtualizar?.();
		} catch (error: unknown) {
			console.error('Erro ao excluir obrigações:', error);
			exibirAlerta(
				'error',
				error instanceof Error ? error.message : 'Erro desconhecido. Por favor, tente novamente.',
				'Erro',
			);
		} finally {
			setIsExcluindo(false);
		}
	};

	useEffect(() => {
		if (obrigacoesSelecionadas.length === 0) {
			setMostrarSelecionados(false);
		}
	}, [obrigacoesSelecionadas, setMostrarSelecionados]);

	return (
		<>
		{drawerVisivel && (
				<div
					className={estilo.mascara}
					onClick={() => setDrawerVisivel(false)} 
				/>
			)}
			<div className={estilo.barraAcoesContainer}>
				<ExcluirPopconfirm
					visivel={popconfirmVisivel}
					onConfirm={confirmarExclusao}
					onCancel={() => setPopconfirmVisivel(false)}
				>
					<Button type='outlined' variant='error' onClick={abrirModalConfirmacao} className={estilo.botaoExcluir}>
						Excluir Obrigações
					</Button>
				</ExcluirPopconfirm>
				<Button type='filled' variant='default' onClick={abrirDrawerEdicao} className={estilo.botaoEditar}>
					Editar Selecionados
				</Button>
			</div>

			{alerta && <CustomAlert key={alerta.key} type={alerta.type} content={alerta.content} title={alerta.title} />}

			<Drawer
				open={drawerVisivel}
				onClose={() => setDrawerVisivel(false)}
				closable={false}
				width='42%' 
				styles={{
					body: { padding: 0 },
					content: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }, 
				}}
				className={estilo.drawerWrapper}
				mask={false}
			>
				<div className={estilo.drawerHeader}>
					<div className={estilo.drawerTitleContainer}>
						<span className={estilo.drawerTitle}>Editar {obrigacoesSelecionadas.length} obrigações</span>
					</div>
					<div className={estilo.drawerActions}>
						<Tooltip title='Limpar modificações'>
							<Button
								type='text'
								icon={<ReloadOutlined className={estilo.iconButton} />}
								onClick={resetForm}
								className={estilo.clearButton}
							/>
						</Tooltip>
						<Tooltip title='Fechar'>
							<Button
								type='text'
								icon={<CloseOutlined className={estilo.iconButton} />}
								onClick={() => setDrawerVisivel(false)}
								className={estilo.closeButton}
							/>
						</Tooltip>
					</div>
				</div>
				<ModalEdicaoEmMassa
					empresaId={empresaId}
					selectedObrigacoes={obrigacoesSelecionadas}
					onClose={() => setDrawerVisivel(false)}
					onAtualizar={aoAtualizar ?? noop}
					resetForm={setResetForm}
					setActiveKeys={setActiveKeys}
					activeKeys={activeKeys}
				/>
			</Drawer>

			<ModalConfirmacao
				visivel={modalConfirmacaoVisivel}
				onConfirm={confirmarExclusao}
				onCancel={() => setModalConfirmacaoVisivel(false)}
				titulo='Confirmação de Exclusão'
				descricao={
					<>
						Você está prestes a excluir todas as <strong>{obrigacoesSelecionadas.length} obrigações</strong>
						selecionadas. Esta ação é irreversível. Deseja continuar?
					</>
				}
				confirmButtonText='Excluir Obrigações'
				isDanger={true}
			/>

			{isExcluindo && (
				<ModalContainer
					isVisible={isExcluindo}
					onClose={() => setIsExcluindo(false)}
					title='Excluindo Obrigações...'
					fechamentoPermitido={progresso === 100}
				>
					<BarraDeProgresso cor='#38939c' progresso={progresso} tempoEstimado={tempoEstimado} />
				</ModalContainer>
			)}
		</>
	);
};

export default BarraAcoes;

const ExcluirPopconfirm: React.FC<{
	visivel: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	children: React.ReactNode;
}> = ({ visivel, onConfirm, onCancel, children }) => (
	<Popconfirm
		title='Você tem certeza que deseja excluir as obrigações selecionadas?'
		open={visivel}
		onConfirm={onConfirm}
		onCancel={onCancel}
		okText='Sim, excluir!'
		cancelText='Cancelar'
	>
		{children}
	</Popconfirm>
);
